import {useState} from "react";
import {Alert, Badge, Button, Card, Table} from "react-bootstrap";
import {ProfileApi} from "../../api/ProfileApi";
import {LicenseActivationViewModel, LicenseViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {CopyButton} from "../../components/CopyButton";
import {formatDateTime} from "../../components/format";

// The account page TSA-1 links to (TSA1_LICENSE_PORTAL): see and free the computers using your licences.
export function MyLicensesPage() {
    const {data: licenses, error, loading, reload} = useAsync(() => ProfileApi.licenses(), []);
    const [freeing, setFreeing] = useState<{ license: LicenseViewModel, activation: LicenseActivationViewModel }>();
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<unknown>();

    const free = async () => {
        if (!freeing) {
            return;
        }
        setBusy(true);
        setActionError(undefined);
        try {
            await ProfileApi.freeSeat(freeing.license.id, freeing.activation.id);
            reload();
        } catch (err) {
            setActionError(err);
        } finally {
            setBusy(false);
            setFreeing(undefined);
        }
    };

    return (
        <>
            <PageHeader title="My licences"/>
            {loading && !licenses && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            <ErrorAlert error={actionError} messages={{ 404: 'That computer was already deactivated.' }} onClose={() => setActionError(undefined)}/>
            {licenses?.length === 0 && <Alert variant="secondary">There are no licences on your account.</Alert>}
            {licenses?.map(license => (
                <Card key={license.id} className="mb-4">
                    <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                        <strong>TSA-1</strong>
                        <Badge bg="secondary">{license.edition}</Badge>
                        {license.revoked && <Badge bg="danger">Revoked</Badge>}
                        <span className="ms-auto d-flex align-items-center gap-2">
                            <span className="license-key">{license.key}</span>
                            <CopyButton text={license.key} label="Copy licence key"/>
                        </span>
                    </Card.Header>
                    <Card.Body>
                        <p className="mb-3">
                            Licensed to {license.licensee}. Active on <strong>{license.activations.length} of {license.seats}</strong> computers;{' '}
                            {license.activationsThisYear} of {license.activationsPerYear} new activations used in the last 12 months.
                        </p>
                        {license.activations.length === 0 ? (
                            <p className="text-body-secondary mb-0">Not active on any computer.</p>
                        ) : (
                            <Table responsive className="mb-0">
                                <thead>
                                <tr><th>Computer</th><th>System</th><th>Version</th><th>Last seen</th><th></th></tr>
                                </thead>
                                <tbody>
                                {license.activations.map(activation => (
                                    <tr key={activation.id}>
                                        <td>{activation.machineName || 'Unnamed computer'}</td>
                                        <td className="small text-body-secondary">{activation.os}</td>
                                        <td>{activation.productVersion}</td>
                                        <td>{formatDateTime(activation.lastSeen)}</td>
                                        <td className="text-end">
                                            <Button size="sm" variant="outline-danger" onClick={() => setFreeing({ license, activation })}>Deactivate</Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            ))}
            <ConfirmModal show={!!freeing} title="Deactivate this computer?" confirmLabel="Deactivate" busy={busy} onConfirm={free} onCancel={() => setFreeing(undefined)}>
                <p>
                    <strong>{freeing?.activation.machineName || 'This computer'}</strong> will stop running TSA-1 the next time it
                    renews its licence online, or when its current lease runs out.
                </p>
                <p className="mb-0 text-body-secondary">
                    Freeing the seat doesn't give back one of the {freeing?.license.activationsPerYear} new activations
                    allowed each year. If you still have the computer, use <em>Deactivate this computer</em> in TSA-1's
                    licence dialog instead: it stops straight away.
                </p>
            </ConfirmModal>
        </>
    );
}
